import { getRequestVerificationToken } from './utilities';

const attributionSessionKey = 'usaj-auth-attribution';
const attributionCapturedKey = 'usaj-auth-attribution-captured';
let capturePromise: Promise<void> | undefined;
let retrievedRequestVerificationToken: string | undefined;

interface AttributionCapture {
    UtmID?: string;
    UtmSource?: string;
    UtmMedium?: string;
    UtmCampaign?: string;
    UtmTerm?: string;
    UtmContent?: string;
    LandingPath: string;
    ReferrerHost?: string;
}

function hasUtmParameters(attribution: AttributionCapture): boolean {
    return [
        attribution.UtmID,
        attribution.UtmSource,
        attribution.UtmMedium,
        attribution.UtmCampaign,
        attribution.UtmTerm,
        attribution.UtmContent
    ].some(value => Boolean(value?.trim()));
}

function externalReferrerHost(): string | undefined {
    if (!document.referrer) {
        return undefined;
    }

    const referrer = new URL(document.referrer);
    return referrer.hostname === window.location.hostname ? undefined : referrer.hostname;
}

function getFirstTouch(): AttributionCapture {
    const existing = sessionStorage.getItem(attributionSessionKey);
    if (existing) {
        try {
             const attribution = JSON.parse(existing) as AttributionCapture;
             if (hasUtmParameters(attribution)) {
                 return attribution;
             }
         } catch {
             sessionStorage.removeItem(attributionSessionKey);
         }
    }

    const query = new URLSearchParams(window.location.search);
    const attribution: AttributionCapture = {
        UtmID: query.get('utm_id') || undefined,
        UtmSource: query.get('utm_source') || undefined,
        UtmMedium: query.get('utm_medium') || undefined,
        UtmCampaign: query.get('utm_campaign') || undefined,
        UtmTerm: query.get('utm_term') || undefined,
        UtmContent: query.get('utm_content') || undefined,
        LandingPath: window.location.pathname,
        ReferrerHost: externalReferrerHost()
    };
    if (hasUtmParameters(attribution)) {
        sessionStorage.setItem(attributionSessionKey, JSON.stringify(attribution));
    }
    return attribution;
}

export function prepareAuthenticationAttribution(): Promise<void> {
    if (sessionStorage.getItem(attributionCapturedKey)) {
        return Promise.resolve();
    }

    if (capturePromise) {
        return capturePromise;
    }

    capturePromise = captureAuthenticationAttribution()
        .finally(() => capturePromise = undefined);
    return capturePromise;
}

async function captureAuthenticationAttribution(): Promise<void> {
    if (sessionStorage.getItem(attributionCapturedKey)) {
        return;
    }

    const attribution = getFirstTouch();
    if (!hasUtmParameters(attribution)) {
        return;
    }

    const token = await getAttributionRequestVerificationToken();
    if (!token) {
        return;
    }

    const response = await fetch('/Account/CaptureAuthenticationAttribution', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            'RequestVerificationToken': token
        },
        body: JSON.stringify(attribution)
    });

    if (response.ok) {
        sessionStorage.setItem(attributionCapturedKey, 'true');
    }
}

async function getAttributionRequestVerificationToken(): Promise<string | undefined> {
    const pageToken = getRequestVerificationToken();
    if (pageToken) {
        return pageToken;
    }

    if (retrievedRequestVerificationToken) {
        return retrievedRequestVerificationToken;
    }

    const response = await fetch('/Account/RetrieveAntiforgeryToken', {
        credentials: 'same-origin'
    });
    if (!response.ok) {
        return undefined;
    }

    retrievedRequestVerificationToken = (await response.text()).trim() || undefined;
    return retrievedRequestVerificationToken;
}

export function clearAuthenticationAttribution(): void {
    sessionStorage.removeItem(attributionSessionKey);
    sessionStorage.removeItem(attributionCapturedKey);
}
